import { Flex } from "@chakra-ui/layout";
import { Heading, Box } from "@chakra-ui/react";
import { Modal } from "antd";
import { useLoading } from "components/GlobalLoading";
import { getTitleAnexoFromTipo } from "core/features/conta/util/getTitleAnexoFromTipo";
import { TipoAnexoContaEnum, ContaAnexo } from "core/features/conta/typings";
import { useState, useEffect } from "react";
import useAnexosConta from "core/features/conta/hooks/useAnexosConta";
import ButtonAnexarArquivos from "ui/components/Button/ButtonAnexarArquivos";
import Anexos from "components/Anexos";
import useUploadAnexo from "core/features/arquivo/useUploadAnexo";
import { v4 as uuidv4 } from "uuid";
import FooterModal from "ui/components/Button/FooterModal";
import { useForm } from "react-hook-form";
import useUploadAnexosConta from "../../../core/features/conta/hooks/useUploadAnexosConta";
import { showSuccess } from "../../../infra/helpers/alerta";

interface ModalAnexosContaProps {
  contaId: string;
  tipoAnexoId: TipoAnexoContaEnum;
  isOpenModal: boolean;
  onClose: () => any;
  refetch?: () => any;
}

export default function ModalAnexosConta({
  contaId,
  tipoAnexoId,
  isOpenModal,
  onClose,
  refetch,
}: ModalAnexosContaProps) {
  const { data, isFetching } = useAnexosConta(contaId, tipoAnexoId);

  const { mutate: uploadAnexos, isLoading: isUploadingAnexos } =
    useUploadAnexosConta();
  const title = getTitleAnexoFromTipo(tipoAnexoId);
  const [disableButtonAnexar, setDisabledButtonAnexar] = useState(false);
  const [deletedAnexosIds, setDeletedAnexosIds] = useState<string[]>([]);

  const { reset, handleSubmit, watch, setValue } = useForm<{
    files: (File | ContaAnexo)[];
  }>({
    defaultValues: {
      files: [],
    },
  });

  useLoading(isFetching || isUploadingAnexos);

  const form = watch();

  useEffect(() => {
    if (data) {
      reset({ files: data });
    }
  }, [data, reset]);

  const onSubmit = (_form: { files: (File | ContaAnexo)[] }) => {
    const _files = _form.files as ContaAnexo[];

    uploadAnexos(
      {
        id: contaId,
        tipoAnexoId,
        anexos: _files.map((file) => ({
          id: file.id!,
          referencia: file.referencia,
          mimetype: file.mimetype,
          nome: file.nome,
        })),
        arquivosExcluidos: deletedAnexosIds,
      },
      {
        onSuccess: () => {
          showSuccess().then(() => {
            onClose();
            refetch?.();
          });
        },
      }
    );
  };

  return (
    <>
      <Modal
        centered
        destroyOnClose
        width="1000px"
        footer={
          <FooterModal
            registerAnother={false}
            setRegisterAnother={() => null}
            onSubmit={handleSubmit(onSubmit)}
            onClose={onClose}
            showRegisterAnother={false}
            primaryButtonText="Salvar"
            buttonCancelarText="Fechar"
            isDisabled={isUploadingAnexos || isFetching || disableButtonAnexar}
          />
        }
        visible={isOpenModal}
        onCancel={onClose}
      >
        <Flex>
          <Heading mb={4} size="md">
            Anexar {title}
          </Heading>
        </Flex>
        <Flex py="4px" flexDir={"column"}>
          <Box flex={1}>
            <ButtonAnexarArquivos
              accept="image/*, application/pdf, video/*, .csv, .xlsx"
              maxFileSizeMb={200}
              currentFilesLength={form?.files?.length || 0}
              variant="ghost"
              disabled={disableButtonAnexar}
              onAnexar={(files) => {
                if (files) {
                  const currentAnexos = form?.files;
                  files.forEach((file) => {
                    if (file instanceof File) {
                      (file as any).key = uuidv4();
                    }
                  });

                  setValue(
                    "files",
                    currentAnexos ? [...currentAnexos, ...files] : [...files]
                  );
                }
              }}
            />
          </Box>

          <Box gap="16px" mt="auto" h="fit-content" py={2} flexDir="column">
            {form?.files?.length ? (
              <Anexos
                onStartUploadMutation={() => setDisabledButtonAnexar(true)}
                onFinishUploadMutation={() => setDisabledButtonAnexar(false)}
                autoUploadMutation={useUploadAnexo}
                trackRemove={(id) => {
                  if (id) {
                    setDeletedAnexosIds((prev) => [...prev, id]);
                  }
                }}
                setValue={(files) => {
                  setValue(
                    "files",
                    files?.map((file) =>
                      Object.assign(file, {
                        nome:
                          file instanceof File || Reflect.has(file, "name")
                            ? file.name
                            : file.nome!,
                      })
                    ) || []
                  );
                }}
                anexos={
                  form?.files?.map((file) =>
                    file instanceof File
                      ? file
                      : { ...file, name: file.nome, id: file.id! }
                  ) || undefined
                }
              />
            ) : null}
          </Box>
        </Flex>
      </Modal>
    </>
  );
}
